import {
  LdsButton,
  LdsModal,
  LdsSelect,
  LdsIcon,
} from "@elilillyco/ux-lds-react";
import React from 'react';
import Select from 'react-select';

import "./../../assets/scss/overview/createListModal.scss";
import { useEffect, useState, useCallback, useMemo } from "react";
import {
  FETCH_THPC_DATA,
  CLEAR_CASCADING_OPTIONS,
  CREATE_LIST,
} from "../../store/createList";
import { FETCH_USER_LIST } from "../../store/admin";
import { getRcSourceLists } from "../../api/overview";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useCascadingOptions } from "../../utils/useCascadingOptions";

const CreateListModal = ({
  isModalOpen,
  setIsModalOpen,
  modalType,
  navigateBack,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Use cascading options hook
  const {
    cascadingOptions,
    cascadingLoading,
    fetchCascadingOptions,
    getFormattedOptions,
    resetCascadingState
  } = useCascadingOptions();

  const { thpcdata, loading, error } = useSelector(({ createlist }) => createlist);
  const { uuid, emailId, brands: userBrands } = useSelector(({ user }) => user);
  const { all_brands: allBrands } = useSelector(({ user }) => user.user || {});
  const { thpc: adminThpcData, thpcLoading, userTableList } = useSelector(({ admin }) => admin);
  
  // Fetch userTableList when modal opens (if not already loaded)
  useEffect(() => {
    if (isModalOpen && (!userTableList || userTableList.length === 0)) {
      dispatch(FETCH_USER_LIST());
    }
  }, [isModalOpen, userTableList, dispatch]);
  
  // Get the correct UUID for the currently selected role
  const currentRole = sessionStorage.getItem("role");
  
  /**
   * Get user's assigned brands/molecules for current role
   * Returns array of brand/molecule names in uppercase
   */
  const getUserAssignedBrands = React.useMemo(() => {
    console.log('Debug - getUserAssignedBrands called');
    console.log('Debug - userBrands:', userBrands);
    console.log('Debug - currentRole:', currentRole);
    console.log('Debug - allBrands:', allBrands);
    
    if (!userBrands || !currentRole) {
      console.log('Debug - No userBrands or currentRole, returning empty array');
      return [];
    }
    
    const brandValue = userBrands[currentRole];
    console.log('Debug - brandValue for role:', brandValue);
    
    if (!brandValue) {
      console.log('Debug - No brandValue for current role, returning empty array');
      return [];
    }
    
    // Check if user has ALL brands
    if (brandValue.trim() === "ALL" && allBrands) {
      const allBrandsList = allBrands.split(",").map(b => b.trim().toUpperCase()).filter(Boolean);
      console.log('Debug - User has ALL brands:', allBrandsList);
      return allBrandsList;
    }
    
    // Return specific assigned brands
    const assignedBrands = brandValue.split(",").map(b => b.trim().toUpperCase()).filter(Boolean);
    console.log('Debug - User has specific brands:', assignedBrands);
    return assignedBrands;
  }, [userBrands, allBrands, currentRole]);
  
  /**
   * Check if user has access to a specific brand/molecule
   */
  const hasBrandAccess = React.useCallback((brandName) => {
    if (!brandName) {
      console.log('Debug - hasBrandAccess: No brandName provided');
      return false;
    }
    
    // Admin and Super Admin have access to ALL brands
    if (currentRole === "spclty_hub_admin" || currentRole === "spclty_hub_super_admin") {
      console.log('Debug - hasBrandAccess: Admin/Super Admin has access to all');
      return true;
    }
    
    const brandUpper = brandName.trim().toUpperCase();
    const hasAccess = getUserAssignedBrands.includes(brandUpper);
    console.log(`Debug - hasBrandAccess: Checking '${brandUpper}' in`, getUserAssignedBrands, '=', hasAccess);
    return hasAccess;
  }, [getUserAssignedBrands, currentRole]);
  
  // Use useMemo to recalculate when userTableList updates
  const currentRoleUuid = useMemo(() => {
    if (!userTableList || userTableList.length === 0) {
      return uuid;
    }
    
    const matchingUser = userTableList.find(
      (user) => user.email_id === emailId && user.role === currentRole && user.active === true
    );
    
    return matchingUser ? matchingUser.uuid : uuid;
  }, [userTableList, emailId, currentRole, uuid]);

  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectListType, setSelectListType] = useState("");
  // selected values
  const [selectedthpc, setSelectedthpc] = useState("");
  const [selecteddsst, setSelecteddsst] = useState("");
  const [selectedindication, setSelectedindication] = useState("");
  const [selectedbrand, setSelectedbrand] = useState("");
  const [selectedmoelcule, setSelectedmoelcule] = useState("");
  const [listQuarter, setListQuarter] = useState("");
  // selected values
  const [isQuarterSelected, setIsQuarterSelected] = useState(false);
  const [isThpcSelected, setIsThpcSelected] = useState(false);
  const [isDsstSelected, setIsDsstSelected] = useState(false);
  const [isMoleculeSelected, setIsMoleculeSelected] = useState(false);
  const [isIndicationSelected, setIsIndicationSelected] = useState(false);
  const [isBrandSelected, setIsBrandSelected] = useState(false);
  // next button disabled
  const [isNextButtonDisabled, setIsNextButtonDisabled] = useState(true);
  // RC Source selection states (for first-time SF list creation)
  const [rcSourceOptions, setRcSourceOptions] = useState([]);
  const [selectedRcSource, setSelectedRcSource] = useState("");
  const [rcSourceLoading, setRcSourceLoading] = useState(false);
  const [hasSameTargetSfSource, setHasSameTargetSfSource] = useState(false);

  // Reset all state values when isModalOpen changes
  useEffect(() => {
    if (!isModalOpen) {
      setSelectedDepartment("");
      setSelectListType("");
      setSelectedthpc("");
      setSelecteddsst("");
      setSelectedindication("");
      setSelectedbrand("");
      setSelectedmoelcule("");
      setListQuarter("");
      setIsQuarterSelected(false);
      setIsThpcSelected(false);
      setIsDsstSelected(false);
      setIsMoleculeSelected(false);
      setIsIndicationSelected(false);
      setIsBrandSelected(false);
      // Reset RC source states
      setRcSourceOptions([]);
      setSelectedRcSource("");
      setRcSourceLoading(false);
      setHasSameTargetSfSource(false);
      // Reset cascading state
      resetCascadingState();
    }
  }, [isModalOpen, resetCascadingState]);

  // Reset other state if selectedDepartment changes
  useEffect(() => {
    setSelectListType("");
    setSelectedthpc("");
    setSelecteddsst("");
    setSelectedindication("");
    setSelectedbrand("");
    setSelectedmoelcule("");
    setListQuarter("");
    setIsQuarterSelected(false);
    setIsThpcSelected(false);
    setIsDsstSelected(false);
    setIsMoleculeSelected(false);
    setIsIndicationSelected(false);
    setIsBrandSelected(false);
    setRcSourceOptions([]);
    setSelectedRcSource("");
    setHasSameTargetSfSource(false);
  }, [selectedDepartment]);

  // Reset all state except selectedDepartment if selectListType changes
  useEffect(() => {
    setSelectedthpc("");
    setSelecteddsst("");
    setSelectedindication("");
    setSelectedbrand("");
    setSelectedmoelcule("");
    setListQuarter("");
    setIsQuarterSelected(false);
    setIsThpcSelected(false);
    setIsDsstSelected(false);
    setIsMoleculeSelected(false);
    setIsIndicationSelected(false);
    setIsBrandSelected(false);
    setRcSourceOptions([]);
    setSelectedRcSource("");
    setHasSameTargetSfSource(false);
    // Reset cascading state
    resetCascadingState();
  }, [selectListType, resetCascadingState]);

  // Note: THPC options now come from admin store (already loaded on overview page)
  // Only fetch cascading options for subsequent fields

  // Fetch existing lists data when modal opens in existing mode
  useEffect(() => {
    if (modalType && isModalOpen && selectedDepartment) {
      console.log("Fetching THPC data for department:", selectedDepartment);
      dispatch(FETCH_THPC_DATA({
        department_field: selectedDepartment,
        search_keyword: "",
        list_type: "", // Fetch all list types for the department
        clone_type: "existing"
      }));
    }
  }, [modalType, isModalOpen, selectedDepartment, dispatch]);

  // Debug effect to log thpcdata changes
  useEffect(() => {
    if (modalType) {
      console.log("THPC data state update:", {
        length: thpcdata?.length || 0,
        data: thpcdata,
        loading: loading,
        error: error
      });
      
      if (error) {
        console.error("THPC data fetch error:", error);
      }
    }
  }, [modalType, thpcdata, loading, error]);

  // Get formatted options for dropdowns
  const getThpcOptions = useCallback(() => {
    // For existing mode, use the filtered THPC data from existing lists
    if (modalType && thpcdata && Array.isArray(thpcdata)) {
      // Filter existing lists based on selected department and list type
      let filteredLists = thpcdata;
      
      if (selectedDepartment && selectListType) {
        filteredLists = thpcdata.filter(item => {
          const matchesDepartment = item.department_field === selectedDepartment;
          const matchesListType = item.list_type === selectListType;
          return matchesDepartment && matchesListType;
        });
        
        console.log("Filtered THPC data:", {
          originalCount: thpcdata.length,
          filteredCount: filteredLists.length,
          department: selectedDepartment,
          listType: selectListType,
          filteredData: filteredLists
        });
      }
      
      // Extract unique THPC names from filtered existing lists
      const uniqueThpcs = [...new Set(filteredLists.map(item => item.thpc_nm))].filter(Boolean);
      return uniqueThpcs.map(thpcName => ({
        label: thpcName,
        value: thpcName
      }));
    }
    
    // For scratch mode, use admin THPC data (all available THPCs)
    if (!modalType && adminThpcData && Array.isArray(adminThpcData)) {
      return adminThpcData.map(item => ({
        label: item.label || item.value || '',
        value: item.value || item.label || ''
      })).filter(item => item.label && item.value);
    }
    
    return [];
  }, [modalType, thpcdata, adminThpcData, selectedDepartment, selectListType]);

  // Helper function to check if THPC options are available for existing mode
  const hasThpcOptionsForSelectedType = useCallback(() => {
    if (!modalType || !selectListType || !selectedDepartment) return true;
    const thpcOptions = getThpcOptions();
    return thpcOptions.length > 0;
  }, [modalType, selectListType, selectedDepartment, getThpcOptions]);

  // is next button disabled - updated to use cascading options
  useEffect(() => {
    // the button disabled state depends on quarter when user is hcp sf marketer or incentivized approver and brand is selected
    const quarterSelected = checkSFRole() ? isQuarterSelected : true;
    
    // Get formatted options for validation
    const thpcOptions = getThpcOptions(); // Use admin data for THPC
    const dsstOptions = getFormattedOptions('disease_state', selectListType, modalType ? "existing" : "scratch");
    const moleculeOptions = getFormattedOptions('molecule', selectListType, modalType ? "existing" : "scratch");
    const brandOptions = getFormattedOptions('brand', selectListType, modalType ? "existing" : "scratch");
    const indicationOptions = getFormattedOptions('indication', selectListType, modalType ? "existing" : "scratch");
    
    if (selectedDepartment === "MEDICAL" && selectListType === "thpconly") {
      setIsNextButtonDisabled(
        !(
          quarterSelected &&
          isThpcSelected &&
          thpcOptions.some((item) => item.value === selectedthpc)
        )
      );
    } else if (
      selectedDepartment === "MEDICAL" &&
      selectListType === "thpc&dsst"
    ) {
      setIsNextButtonDisabled(
        !(
          quarterSelected &&
          isThpcSelected &&
          isDsstSelected &&
          thpcOptions.some((item) => item.value === selectedthpc) &&
          dsstOptions.some((item) => item.value === selecteddsst)
        )
      );
    } else if (
      selectedDepartment === "MEDICAL" &&
      selectListType === "thpc&dsst&mdpr"
    ) {
      setIsNextButtonDisabled(
        !(
          quarterSelected &&
          isThpcSelected &&
          isDsstSelected &&
          isMoleculeSelected &&
          thpcOptions.some((item) => item.value === selectedthpc) &&
          dsstOptions.some((item) => item.value === selecteddsst) &&
          moleculeOptions.some((item) => item.value === selectedmoelcule)
        )
      );
    } else if (
      selectedDepartment === "RETAIL" &&
      selectListType === "brnd&ind"
    ) {
      setIsNextButtonDisabled(
        !(
          quarterSelected &&
          isThpcSelected &&
          isIndicationSelected &&
          isBrandSelected &&
          isDsstSelected &&
          thpcOptions.some((item) => item.value === selectedthpc) &&
          dsstOptions.some((item) => item.value === selecteddsst) &&
          indicationOptions.some((item) => item.value === selectedindication) &&
          brandOptions.some((item) => item.value === selectedbrand)
        )
      );
    } else if (
      selectedDepartment === "RETAIL" &&
      selectListType === "thpc&dsst&mdpr"
    ) {
      setIsNextButtonDisabled(
        !(
          quarterSelected &&
          isThpcSelected &&
          isDsstSelected &&
          isMoleculeSelected &&
          thpcOptions.some((item) => item.value === selectedthpc) &&
          dsstOptions.some((item) => item.value === selecteddsst) &&
          moleculeOptions.some((item) => item.value === selectedmoelcule)
        )
      );
    }
  }, [
    isBrandSelected,
    isIndicationSelected,
    isQuarterSelected,
    isThpcSelected,
    isDsstSelected,
    isMoleculeSelected,
    selectedthpc,
    selecteddsst,
    selectedindication,
    selectedbrand,
    selectedmoelcule,
    listQuarter,
    modalType,
    selectListType,
    selectedDepartment,
    cascadingOptions,
    getFormattedOptions,
    getThpcOptions,
  ]);

  // Handle cascading when THPC changes
  useEffect(() => {
    if (selectedthpc && selectedDepartment && selectListType) {
      // Clear dependent selections safely
      setSelecteddsst("");
      setSelectedbrand("");
      setSelectedindication("");
      setSelectedmoelcule("");
      
      // Clear dependent selection states
      setIsDsstSelected(false);
      setIsBrandSelected(false);
      setIsIndicationSelected(false);
      setIsMoleculeSelected(false);
      
      // Clear dependent cascading options
      dispatch(CLEAR_CASCADING_OPTIONS({ 
        fieldTypes: ['disease_state', 'molecule', 'brand', 'indication'] 
      }));
      
      // Fetch disease state options
      fetchCascadingOptions({
        field_type: 'disease_state',
        department_field: selectedDepartment,
        list_type: selectListType,
        clone_type: modalType ? "existing" : "scratch",
        thpc_nm: selectedthpc
      });
    }
  }, [selectedthpc, selectedDepartment, selectListType, modalType, fetchCascadingOptions, dispatch]);

  // Handle cascading when disease state changes
  useEffect(() => {
    if (selecteddsst && selectedthpc && selectedDepartment && selectListType) {
      // Clear dependent selections safely
      setSelectedbrand("");
      setSelectedindication("");
      setSelectedmoelcule("");
      
      // Clear dependent selection states
      setIsBrandSelected(false);
      setIsIndicationSelected(false);
      setIsMoleculeSelected(false);
      
      // Clear dependent cascading options
      dispatch(CLEAR_CASCADING_OPTIONS({ 
        fieldTypes: ['molecule', 'brand', 'indication'] 
      }));
      
      // Fetch molecule and brand options based on list type
      if (selectListType === "thpc&dsst&mdpr") {
        fetchCascadingOptions({
          field_type: 'molecule',
          department_field: selectedDepartment,
          list_type: selectListType,
          clone_type: modalType ? "existing" : "scratch",
          thpc_nm: selectedthpc,
          dsst_nm: selecteddsst
        });
      }
      
      if (selectListType === "brnd&ind") {
        fetchCascadingOptions({
          field_type: 'brand',
          department_field: selectedDepartment,
          list_type: selectListType,
          clone_type: modalType ? "existing" : "scratch",
          thpc_nm: selectedthpc,
          dsst_nm: selecteddsst
        });
      }
    }
  }, [selecteddsst, selectedthpc, selectedDepartment, selectListType, modalType, fetchCascadingOptions, dispatch]);

  // Handle cascading when brand changes (for indication)
  useEffect(() => {
    if (selectedbrand && selecteddsst && selectedthpc && selectedDepartment && selectListType === "brnd&ind") {
      // Clear dependent selections safely
      setSelectedindication("");
      
      // Clear dependent selection state
      setIsIndicationSelected(false);
      
      // Clear dependent cascading options
      dispatch(CLEAR_CASCADING_OPTIONS({ 
        fieldTypes: ['indication'] 
      }));
      
      // Fetch indication options
      fetchCascadingOptions({
        field_type: 'indication',
        department_field: selectedDepartment,
        list_type: selectListType,
        clone_type: modalType ? "existing" : "scratch",
        thpc_nm: selectedthpc,
        dsst_nm: selecteddsst,
        brand_nm: selectedbrand
      });
    }
  }, [selectedbrand, selecteddsst, selectedthpc, selectedDepartment, selectListType, modalType, fetchCascadingOptions, dispatch]);

  // Fetch available RC source lists when creating an SF list (quarter selected)
  // This provides the "Copy RC Codes From" dropdown for first-time SF list creation
  useEffect(() => {
    if (checkSFRole() && listQuarter && selectedDepartment && selectedthpc) {
      setRcSourceLoading(true);
      setSelectedRcSource("");

      const rcSourceParams = {
        department_field: selectedDepartment,
        list_type: selectListType,
      };

      // Custom filtering requirements for these two list types only:
      // - thpc&dsst: fetch only this list type and THPC-related DSST SF lists
      // - thpconly: fetch only this list type (all THPCs), then exclude selected THPC in UI
      if (selectListType === "thpc&dsst") {
        rcSourceParams.thpc_nm = selectedthpc;
      } else if (selectListType !== "thpconly") {
        // Keep existing behavior for other list types
        rcSourceParams.thpc_nm = selectedthpc;
        rcSourceParams.dsst_nm = selecteddsst || undefined;
      }

      getRcSourceLists(rcSourceParams)
        .then((response) => {
          const lists = response?.data?.data || [];

          // Check if an approved SF source already exists for current target combination.
          // If yes, backend will auto-pick latest same-target SF list, so dropdown should be hidden.
          let sameTargetExists = false;
          if (selectListType === "brnd&ind") {
            sameTargetExists = lists.some(
              (item) => item.thpc_nm === selectedthpc && item.dsst_nm === selecteddsst && item.indication_nm === selectedindication && item.brand_nm === selectedbrand
            );
          } else if (selectListType === "thpc&dsst&mdpr") {
            sameTargetExists = lists.some((item) => item.mdpr_nm === selectedmoelcule);
          } else if (selectListType === "thpc&dsst" || selectListType === "thpconly") {
            if (selectListType === "thpc&dsst") {
              sameTargetExists = lists.some(
                (item) => item.thpc_nm === selectedthpc && item.dsst_nm === selecteddsst
              );
            } else {
              sameTargetExists = lists.some((item) => item.thpc_nm === selectedthpc);
            }
          }

          setHasSameTargetSfSource(sameTargetExists);

          // Filter out the current brand/molecule/indication so user picks a DIFFERENT source
          const filteredLists = lists.filter((item) => {
            // For brand & indication list type
            if (selectListType === "brnd&ind") {
              return (
                item.thpc_nm === selectedthpc &&
                item.dsst_nm === selecteddsst &&
                item.indication_nm === selectedindication &&
                item.brand_nm !== selectedbrand
              );
            }
            // For molecule-based list types
            if (selectListType === "thpc&dsst&mdpr") {
              return item.mdpr_nm !== selectedmoelcule;
            }
            // For THPC + DSST, keep only same THPC and exclude selected DSST
            if (selectListType === "thpc&dsst") {
              return item.thpc_nm === selectedthpc && item.dsst_nm !== selecteddsst;
            }
            // For THPC only, show only different THPC values
            if (selectListType === "thpconly") {
              return item.thpc_nm !== selectedthpc;
            }
            // For other types, show all
            return true;
          });
          
          setRcSourceOptions(filteredLists);
          setRcSourceLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching RC source lists:", err);
          setRcSourceOptions([]);
          setHasSameTargetSfSource(false);
          setRcSourceLoading(false);
        });
    } else {
      setRcSourceOptions([]);
      setSelectedRcSource("");
      setHasSameTargetSfSource(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listQuarter, selectedDepartment, selectedthpc, selecteddsst, selectedbrand, selectedmoelcule, selectedindication, selectListType]);

  const checkSFRole = () => {
    // Only HCP SF Marketer - INC cannot create lists
    if (
      sessionStorage.getItem("role") === "spclty_hub_hcp_sf_marketer"
    ) {
      return true;
    }
    return false;
  };

  /**
   * Gets department options based on user role
   * HCP Marketer: Only Commercial (RETAIL)
   * Omnichannel Advisor: Only Medical (MEDICAL)
   * Other roles: Both options
   */
  const getDepartmentOptions = () => {
    const role = sessionStorage.getItem("role");
    
    const allOptions = [
      {
        value: "",
        label: "Select",
      },
      {
        value: "RETAIL",
        label: "Commercial",
      },
      {
        value: "MEDICAL",
        label: "Medical",
      },
    ];

    // HCP Marketer can only create Commercial lists and only MLR lists
    if (role === "spclty_hub_hcp_marketer") {
      return [
        { value: "", label: "Select" },
        { value: "RETAIL", label: "Commercial" }
      ];
    }

    // Omnichannel Advisor can only create Medical lists
    if (role === "spclty_hub_omnichannel_advisor") {
      return [
        { value: "", label: "Select" },
        { value: "MEDICAL", label: "Medical" }
      ];
    }

    // All other roles see both options
    return allOptions;
  };
  // thpc functions - updated to use cascading options
  const handlethpcChange = (e) => {
    try {
      const value = e?.value || "";
      setSelectedthpc(value);
      if (value) {
        setIsThpcSelected(true);
      } else {
        setIsThpcSelected(false);
      }
    } catch (error) {
      console.error("Error in handlethpcChange:", error);
      setSelectedthpc("");
      setIsThpcSelected(false);
    }
  };

  
  // dsst functions - simplified for cascading
  const handledsstChange = (value) => {
    setSelecteddsst(value);
    if (value) {
      setIsDsstSelected(true);
    } else {
      setIsDsstSelected(false);
    }
  };
  // Simplified handlers for other fields
  const handlemoleculeChange = (value) => {
    setSelectedmoelcule(value);
    if (value) {
      setIsMoleculeSelected(true);
    } else {
      setIsMoleculeSelected(false);
    }
  };

  const handleBrandChange = (value) => {
    setSelectedbrand(value);
    if (value) {
      setIsBrandSelected(true);
    } else {
      setIsBrandSelected(false);
    }
  };

  const handleIndicationChange = (value) => {
    setSelectedindication(value);
    if (value) {
      setIsIndicationSelected(true);
    } else {
      setIsIndicationSelected(false);
    }
  };

  // quarter functions
  const handleQuarter = ({ value }) => {
    setListQuarter(value);
    setIsQuarterSelected(!!value);
  };

  // RC Source handler
  const handleRcSourceChange = (option) => {
    setSelectedRcSource(option ? option.value : "");
  };

  // Format RC source options for the dropdown
  const getRcSourceDropdownOptions = useCallback(() => {
    return rcSourceOptions.map((item) => ({
      label: item.list_title || `${item.brand_nm || item.mdpr_nm || ''} - ${item.list_quarter || ''}`,
      value: item.uuid
    }));
  }, [rcSourceOptions]);

  // Render the "Copy RC Codes From" dropdown (reusable across sections)
  const renderRcSourceDropdown = () => {
    if (!checkSFRole() || !listQuarter) return null;
    if (hasSameTargetSfSource) return null;
    
    const options = getRcSourceDropdownOptions();
    if (options.length === 0 && !rcSourceLoading) return null;
    
    return (
      <div className="col col-12 col-md-4 mt-3">
        <label title="Select an existing approved Salesforce list from a different brand/molecule to copy Responsibility Codes. Useful when creating a Salesforce list for the first time.">
          Copy RC Codes From (Optional) <LdsIcon name="InfoFill" inline style={{ width: '14px', height: '14px', cursor: 'help' }} />
        </label>
        <Select
          id="rcSourceSelect"
          value={options.find(opt => opt.value === selectedRcSource) || null}
          onChange={handleRcSourceChange}
          options={options}
          isSearchable={true}
          isClearable={true}
          isLoading={rcSourceLoading}
          placeholder="Select a list to copy RC codes from..."
          className="custom-select"
          styles={{
            control: (base) => ({
              ...base,
              minHeight: '36px',
            })
          }}
        />
      </div>
    );
  };

  // Format options for dropdown display with brand filtering
  const formatOptionsForDropdown = (options, fieldType) => {
    if (!options || !Array.isArray(options)) return [];
    
    let filteredOptions = options;
    
    // Filter brand and molecule options based on user access
    if (fieldType === 'brand' || fieldType === 'molecule' || fieldType === 'mdpr') {
      console.log(`Debug - Filtering ${fieldType} options. Original count:`, options.length);
      console.log(`Debug - User assigned brands:`, getUserAssignedBrands);
      console.log(`Debug - Current role:`, currentRole);
      
      filteredOptions = options.filter(item => {
        let brandName = '';
        
        // Extract brand/molecule name based on field type
        if (fieldType === 'brand') {
          brandName = item.brnd_nm || item.brand_nm || '';
        } else if (fieldType === 'molecule' || fieldType === 'mdpr') {
          brandName = item.molecule_nm || item.mdpr_nm || '';
        }
        
        const hasAccess = hasBrandAccess(brandName);
        console.log(`Debug - Checking ${fieldType} '${brandName}':`, hasAccess);
        
        // Only include if user has access
        return hasAccess;
      });
      
      console.log(`Debug - Filtered ${fieldType} options. Final count:`, filteredOptions.length);
    }
    
    return filteredOptions.map(item => {
      if (!item || typeof item !== 'object') return null;
      
      let label = '';
      let value = '';
      
      // Map based on field type and the actual data structure
      switch(fieldType) {
        case 'thpc':
          label = item.thpc_nm || '';
          value = item.thpc_nm || '';
          break;
        case 'dsst':
        case 'disease_state':
          label = item.disease_nm || item.dsst_nm || '';
          value = item.disease_nm || item.dsst_nm || '';
          break;
        case 'mdpr':
        case 'molecule':
          label = item.molecule_nm || item.mdpr_nm || '';
          value = item.molecule_nm || item.mdpr_nm || '';
          break;
        case 'brand':
          label = item.brnd_nm || item.brand_nm || '';
          value = item.brnd_nm || item.brand_nm || '';
          break;
        case 'indication':
          label = item.indctn_grp_nm || item.indication_nm || '';
          value = item.indctn_grp_nm || item.indication_nm || '';
          break;
        default:
          // Fallback to generic mapping
          label = item.thpc_nm || item.disease_nm || item.molecule_nm || item.brnd_nm || item.indctn_grp_nm || item.name || item.label || '';
          value = item.thpc_nm || item.disease_nm || item.molecule_nm || item.brnd_nm || item.indctn_grp_nm || item.name || item.value || '';
      }
      
      return { label, value };
    }).filter(item => item && item.label && item.value);
  };

  // Helper function to get ID from cascading options
  const getIdFromCascadingOptions = (fieldType, selectedValue) => {
    if (!selectedValue) return "";
    
    let options = [];
    let item = null;
    
    switch(fieldType) {
      case 'thpc':
        // For THPC, get from any available cascading options (they all contain thpc_id)
        options = cascadingOptions.disease_state || cascadingOptions.molecule || 
                 cascadingOptions.brand || cascadingOptions.indication || [];
        
        if (options.length > 0) {
          console.log(`Debug - Looking for ${fieldType} ID. Options available:`, options.length);
          item = options.find(opt => opt.thpc_nm === selectedValue);
          console.log(`Debug - Found ${fieldType} item:`, item);
          const extractedId = item?.thpc_id || "";
          console.log(`Debug - Extracted ${fieldType} ID:`, extractedId);
          return extractedId;
        }
        console.log(`Debug - No ${fieldType} options available`);
        return "";
        
      case 'dsst':
      case 'disease_state':
        options = cascadingOptions.disease_state || [];
        console.log(`Debug - Looking for ${fieldType} ID. Options available:`, options.length);
        item = options.find(opt => opt.disease_nm === selectedValue || opt.dsst_nm === selectedValue);
        console.log(`Debug - Found ${fieldType} item:`, item);
        const dsstId = item?.dsst_id || "";
        console.log(`Debug - Extracted ${fieldType} ID:`, dsstId);
        return dsstId;
        
      case 'mdpr':
      case 'molecule':
        options = cascadingOptions.molecule || [];
        console.log(`Debug - Looking for ${fieldType} ID. Options available:`, options.length);
        item = options.find(opt => opt.molecule_nm === selectedValue || opt.mdpr_nm === selectedValue);
        console.log(`Debug - Found ${fieldType} item:`, item);
        const moleculeId = item?.mdpr_id || "";
        console.log(`Debug - Extracted ${fieldType} ID:`, moleculeId);
        return moleculeId;
        
      case 'brand':
        options = cascadingOptions.brand || [];
        console.log(`Debug - Looking for ${fieldType} ID. Options available:`, options.length);
        item = options.find(opt => opt.brnd_nm === selectedValue || opt.brand_nm === selectedValue);
        console.log(`Debug - Found ${fieldType} item:`, item);
        const brandId = item?.brnd_id || "";
        console.log(`Debug - Extracted ${fieldType} ID:`, brandId);
        return brandId;
        
      case 'indication':
        options = cascadingOptions.indication || [];
        console.log(`Debug - Looking for ${fieldType} ID. Options available:`, options.length);
        item = options.find(opt => 
          opt.indctn_nm === selectedValue || 
          opt.indication_nm === selectedValue || 
          opt.indctn_grp_nm === selectedValue
        );
        console.log(`Debug - Found ${fieldType} item:`, item);
        const indicationId = item?.indctn_id || item?.indctn_grp_id || "";
        console.log(`Debug - Extracted ${fieldType} ID:`, indicationId);
        return indicationId;
        
      default:
        console.log(`Debug - Unknown field type: ${fieldType}`);
        return "";
    }
  };

  // create list
  const createNext = async () => {
    const listType = selectListType ? selectListType : "";
    let obj = {};
    
    // Validate that thpcdata is loaded when in existing mode
    if (modalType) {
      console.log("Validating THPC data for existing list creation...");
      console.log("Current state:", {
        thpcdata: thpcdata,
        thpcdataLength: thpcdata?.length || 0,
        loading: loading,
        error: error,
        selectedDepartment: selectedDepartment,
        selectListType: selectListType
      });
      
      if (loading) {
        console.log("THPC data is still loading. Please wait...");
        return;
      }
      
      if (error) {
        console.error("THPC data fetch failed with error:", error);
        console.log("Failed to load existing lists. Please try refreshing the page.");
        return;
      }
      
      if (!thpcdata || thpcdata.length === 0) {
        console.error("THPC data is empty. This could mean:");
        console.error("1. No existing lists found for department:", selectedDepartment);
        console.error("2. API call failed silently");
        console.error("3. Data structure is different than expected");
        console.log("No existing lists found. Please check if there are existing lists for this department or try creating from scratch.");
        return;
      }
      
      console.log("THPC data validation passed. Available existing lists:", thpcdata.length);
    }
    
    if (modalType) {
      if (selectedDepartment === "RETAIL") {
        if (selectListType === "brnd&ind") {
          const uuid_old = thpcdata.find(
            (item) =>
              item.thpc_nm === selectedthpc &&
              item.dsst_nm === selecteddsst &&
              item.indication_nm === selectedindication &&
              item.brand_nm === selectedbrand
          )?.uuid;
          
          if (!uuid_old) {
            console.error("Could not find matching existing list for:", {
              thpc_nm: selectedthpc,
              dsst_nm: selecteddsst,
              indication_nm: selectedindication,
              brand_nm: selectedbrand,
              thpcdata: thpcdata
            });
            console.log("Could not find matching existing list. Please try again.");
            return;
          }
          
          obj = {
            clone_type: "existing",
            uuid_old: uuid_old,
            list_quarter: listQuarter || "",
            created_by: currentRoleUuid,
            updated_by: currentRoleUuid,
          };
        } else if (selectListType === "thpc&dsst&mdpr") {
          const uuid_old = thpcdata.find(
            (item) =>
              item.list_type === "thpc&dsst&mdpr" &&
              item.thpc_nm === selectedthpc &&
              item.dsst_nm === selecteddsst &&
              item.mdpr_nm === selectedmoelcule
          )?.uuid;
          
          if (!uuid_old) {
            console.error("Could not find matching existing list for:", {
              list_type: "thpc&dsst&mdpr",
              thpc_nm: selectedthpc,
              dsst_nm: selecteddsst,
              mdpr_nm: selectedmoelcule,
              thpcdata: thpcdata
            });
            console.log("Could not find matching existing list. Please try again.");
            return;
          }
          
          obj = {
            clone_type: "existing",
            uuid_old: uuid_old,
            list_quarter: listQuarter || "",
            created_by: currentRoleUuid,
            updated_by: currentRoleUuid,
          };
        }
      } else {
        if (selectListType === "thpconly") {
          const uuid_old = thpcdata.find(
            (item) =>
              item.list_type === "thpconly" && item.thpc_nm === selectedthpc
          )?.uuid;
          
          if (!uuid_old) {
            console.error("Could not find matching existing list for:", {
              list_type: "thpconly",
              thpc_nm: selectedthpc,
              thpcdata: thpcdata
            });
            console.log("Could not find matching existing list. Please try again.");
            return;
          }
          
          obj = {
            clone_type: "existing",
            uuid_old: uuid_old,
            list_quarter: listQuarter || "",
            created_by: currentRoleUuid,
            updated_by: currentRoleUuid,
          };
        } else if (selectListType === "thpc&dsst") {
          const uuid_old = thpcdata.find(
            (item) =>
              item.list_type === "thpc&dsst" &&
              item.thpc_nm === selectedthpc &&
              item.dsst_nm === selecteddsst
          )?.uuid;
          
          if (!uuid_old) {
            console.error("Could not find matching existing list for:", {
              list_type: "thpc&dsst",
              thpc_nm: selectedthpc,
              dsst_nm: selecteddsst,
              thpcdata: thpcdata
            });
            console.log("Could not find matching existing list. Please try again.");
            return;
          }
          
          obj = {
            clone_type: "existing",
            uuid_old: uuid_old,
            list_quarter: listQuarter || "",
            created_by: currentRoleUuid,
            updated_by: currentRoleUuid,
          };
        } else if (selectListType === "thpc&dsst&mdpr") {
          const uuid_old = thpcdata.find(
            (item) =>
              item.list_type === "thpc&dsst&mdpr" &&
              item.thpc_nm === selectedthpc &&
              item.dsst_nm === selecteddsst &&
              item.mdpr_nm === selectedmoelcule
          )?.uuid;
          
          if (!uuid_old) {
            console.error("Could not find matching existing list for:", {
              list_type: "thpc&dsst&mdpr",
              thpc_nm: selectedthpc,
              dsst_nm: selecteddsst,
              mdpr_nm: selectedmoelcule,
              thpcdata: thpcdata
            });
            console.log("Could not find matching existing list. Please try again.");
            return;
          }
          
          obj = {
            clone_type: "existing",
            uuid_old: uuid_old,
            list_quarter: listQuarter || "",
            created_by: currentRoleUuid,
            updated_by: currentRoleUuid,
          };
        }
      }
    } else {
      // Use cascading options for ID extraction in scratch mode
      obj = {
        thpc_nm: selectedthpc,
        thpc_id: getIdFromCascadingOptions('thpc', selectedthpc),
        dsst_nm: selecteddsst,
        dsst_id: getIdFromCascadingOptions('dsst', selecteddsst),
        indication_nm: selectedindication,
        indication_id: getIdFromCascadingOptions('indication', selectedindication),
        brand_nm: selectedbrand,
        brand_id: getIdFromCascadingOptions('brand', selectedbrand),
        mdpr_nm: selectedmoelcule,
        mdpr_id: getIdFromCascadingOptions('mdpr', selectedmoelcule),
        list_quarter: listQuarter || "",
        list_type: listType || "",
        clone_type: modalType ? "existing" : "scratch",
        department_field: selectedDepartment,
        created_by: currentRoleUuid,
        updated_by: currentRoleUuid,
      };
    }
    // If creating an SF list and user selected an RC source from another brand, include it
    if (selectedRcSource && listQuarter) {
      obj.rc_source_list_uuid = selectedRcSource;
    }
    const response = await dispatch(CREATE_LIST(obj));
    if (response?.payload?.data?.data?.uuid) {
      navigate(`/details/${response.payload.data.data.uuid}`);
    } else {
      console.error("listUUID not found in response");
    }
  };

  return (
    <LdsModal
      open={isModalOpen}
      setModalOpen={setIsModalOpen}
      modalId="createListModal"
      closeBtnAriaLabel="Close"
      className="text-center col-12"
    >
      <LdsButton
        clearDefaultClasses
        aria-label="Back"
        classes="lds-button-clear-style lds-modal-close-btn icon back-button"
        onClick={navigateBack}
      >
        <LdsIcon name="ArrowLeft" label="Back" />
      </LdsButton>
      <div>
        {modalType ? (
          <h2>Create list from existing list.</h2>
        ) : (
          <h2>Create list from scratch.</h2>
        )}
      </div>
      <div className="row">
        <div className="col-4">
          <LdsSelect
            id="departmentSelect"
            label="Department"
            name="departmentSelect"
            value={selectedDepartment}
            options={getDepartmentOptions()}
            onChange={(option) => {
              setSelectedDepartment(option.value);
              setSelectListType("");
            }}
          />
        </div>
        {selectedDepartment === "MEDICAL" && (
          <div className="col-6">
            <LdsSelect
              id="listtype"
              label="List Type"
              name="listtype"
              value={selectListType}
              options={[
                {
                  value: "",
                  label: "Select",
                },
                {
                  value: "thpconly",
                  label: "Therapeutic Area only",
                },
                {
                  value: "thpc&dsst",
                  label: "Therapeutic Area + Disease State",
                },
                {
                  value: "thpc&dsst&mdpr",
                  label: "Therapeutic Area + Disease State + Molecule",
                },
              ]}
              onChange={(option) => {
                setSelectListType(option.value);
              }}
            />
          </div>
        )}
        {selectedDepartment === "RETAIL" && (
          <div className="col-6">
            <LdsSelect
              id="listtype"
              label="List Type"
              name="listtype"
              value={selectListType}
              options={[
                {
                  value: "",
                  label: "Select",
                },
                {
                  value: "brnd&ind",
                  label: "Therapeutic Area + Disease State + Brand + Indication",
                },
                {
                  value: "thpc&dsst&mdpr",
                  label: "Therapeutic Area + Disease State + Molecule",
                },
              ]}
              onChange={(option) => {
                setSelectListType(option.value);
              }}
            />
          </div>
        )}
      </div>
      {selectedDepartment === "MEDICAL" && (
        <div className="row mt-3">
          {selectListType && (
            <div className="col-4">
              Therapeutic Area
              <Select
                id="selectedThpc"
                name="selectedThpc"
                value={getThpcOptions().find(option => option.value === selectedthpc) || null}
                onChange={(option) => handlethpcChange(option)}
                options={[
                  { label: "Select Therapeutic Area", value: "" },
                  ...getThpcOptions()
                ]}
                isDisabled={selectedDepartment === "" || !selectListType || (modalType ? loading || !thpcdata?.length || !hasThpcOptionsForSelectedType() : thpcLoading)}
                isSearchable={true}
                isClearable={true}
                className="custom-select"
                placeholder="Select Therapeutic Area"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '36px',
                    maxHeight: '108px',
                    overflowY: 'auto',
                    whiteSpace: 'normal'
                  })
                }}
              />
            </div>
          )}
          {(selectListType === "thpc&dsst" ||
            selectListType === "thpc&dsst&mdpr") && (
            <div className="col-4">
              <label>Disease State</label>
              <Select
                id="selectedDsst"
                value={formatOptionsForDropdown(cascadingOptions.disease_state || [], 'dsst').find(option => option.value === selecteddsst) || null}
                onChange={(option) => handledsstChange(option ? option.value : "")}
                options={formatOptionsForDropdown(cascadingOptions.disease_state || [], 'dsst')}
                isSearchable={true}
                isClearable={true}
                isLoading={cascadingLoading.disease_state}
                isDisabled={!selectedthpc}
                placeholder="Select Disease State"
                className="custom-select"
              />
            </div>
          )}
          {selectListType === "thpc&dsst&mdpr" && (
            <div className="col-4">
              <label>Molecule</label>
              <Select
                id="selectedMolecule"
                value={formatOptionsForDropdown(cascadingOptions.molecule || [], 'mdpr').find(option => option.value === selectedmoelcule) || null}
                onChange={(option) => handlemoleculeChange(option ? option.value : "")}
                options={formatOptionsForDropdown(cascadingOptions.molecule || [], 'mdpr')}
                isSearchable={true}
                isClearable={true}
                isLoading={cascadingLoading.molecule}
                isDisabled={!selecteddsst}
                placeholder="Select Molecule"
                className="custom-select"
              />
            </div>
          )}

          {checkSFRole() ? (
            <div
              className={`col col-12 ${
                checkSFRole() ? "col-md-2" : "col-md-6"
              }`}
            >
              Select Quarter<sup>*</sup>
              <LdsSelect
                id="list_quarter"
                name="list_quarter"
                options={[
                  {
                    label: "Select",
                    value: "",
                  },
                  {
                    label: "Q1",
                    value: "Q1",
                  },
                  {
                    label: "Q2",
                    value: "Q2",
                  },
                  {
                    label: "Q3",
                    value: "Q3",
                  },
                  {
                    label: "Q4",
                    value: "Q4",
                  },
                ]}
                required={true}
                value={listQuarter}
                onChange={handleQuarter}
              ></LdsSelect>
            </div>
          ) : (
            <></>
          )}
          {renderRcSourceDropdown()}
        </div>
      )}
      {selectedDepartment === "RETAIL" && selectListType === "brnd&ind" && (
        <div className="row mt-3">
          <div className="col-3">
            Therapeutic Area
            <Select
                id="selectedThpc"
                name="selectedThpc"
                value={getThpcOptions().find(option => option.value === selectedthpc) || null}
                onChange={(option) => handlethpcChange(option)}
                options={[
                  { label: "Select Therapeutic Area", value: "" },
                  ...getThpcOptions()
                ]}
                isDisabled={selectedDepartment === "" || !selectListType || (modalType ? loading || !thpcdata?.length || !hasThpcOptionsForSelectedType() : thpcLoading)}
                isSearchable={true}
                isClearable={true}
                className="custom-select"
                placeholder="Select Therapeutic Area"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '36px',
                    maxHeight: '108px',
                    overflowY: 'auto',
                    whiteSpace: 'normal'
                  })
                }}
              />
            {/* <LdsSelect
              id="selectedThpc"
              name="selectedThpc"
              label=""
              value={selectedthpc}
              onChange={handlethpcChange}
              options={[
                { label: "Select Therapeutic Area", value: "" },
                ...getThpcOptions()
              ]}
              disabled={selectedDepartment === "" || thpcLoading}
            /> */}
          </div>
          <div className="col-3">
            <label>Disease State</label>
            <Select
              id="selectedDsst"
              value={formatOptionsForDropdown(cascadingOptions.disease_state || [], 'dsst').find(option => option.value === selecteddsst) || null}
              onChange={(option) => handledsstChange(option ? option.value : "")}
              options={formatOptionsForDropdown(cascadingOptions.disease_state || [], 'dsst')}
              isSearchable={true}
              isClearable={true}
              isLoading={cascadingLoading.disease_state}
              isDisabled={!selectedthpc}
              placeholder="Select Disease State"
              className="custom-select"
            />
          </div>
          <div className="col-3">
            <label>Brand</label>
            <Select
              id="selectedBrand"
              value={formatOptionsForDropdown(cascadingOptions.brand || [], 'brand').find(option => option.value === selectedbrand) || null}
              onChange={(option) => handleBrandChange(option ? option.value : "")}
              options={formatOptionsForDropdown(cascadingOptions.brand || [], 'brand')}
              isSearchable={true}
              isClearable={true}
              isLoading={cascadingLoading.brand}
              isDisabled={!selecteddsst}
              placeholder="Select Brand"
              className="custom-select"
            />
          </div>          
          <div className="col-3">
            <label>Indication</label>
            <Select
              id="selectedIndication"
              value={formatOptionsForDropdown(cascadingOptions.indication || [], 'indication').find(option => option.value === selectedindication) || null}
              onChange={(option) => handleIndicationChange(option ? option.value : "")}
              options={formatOptionsForDropdown(cascadingOptions.indication || [], 'indication')}
              isSearchable={true}
              isClearable={true}
              isLoading={cascadingLoading.indication}
              isDisabled={!selectedbrand}
              placeholder="Select Indication"
              className="custom-select"
            />
          </div>
          {checkSFRole() ? (
            <div
              className={`col col-12 ${
                checkSFRole() ? "col-md-2" : "col-md-6"
              }`}
            >
              Select Quarter<sup>*</sup>
              <LdsSelect
                id="list_quarter"
                name="list_quarter"
                options={[
                  {
                    label: "Select",
                    value: "",
                  },
                  {
                    label: "Q1",
                    value: "Q1",
                  },
                  {
                    label: "Q2",
                    value: "Q2",
                  },
                  {
                    label: "Q3",
                    value: "Q3",
                  },
                  {
                    label: "Q4",
                    value: "Q4",
                  },
                ]}
                required={true}
                value={listQuarter}
                onChange={handleQuarter}
              ></LdsSelect>
            </div>
          ) : (
            <></>
          )}
          {renderRcSourceDropdown()}
        </div>
      )}
      {selectedDepartment === "RETAIL" &&
        selectListType === "thpc&dsst&mdpr" && (
          <div className="row mt-3">
            <div className="col-4">
              Therapeutic Area
              <Select
                id="selectedThpc"
                name="selectedThpc"
                value={getThpcOptions().find(option => option.value === selectedthpc) || null}
                onChange={(option) => handlethpcChange(option)}
                options={[
                  { label: "Select Therapeutic Area", value: "" },
                  ...getThpcOptions()
                ]}
                isDisabled={selectedDepartment === "" || !selectListType || (modalType ? loading || !thpcdata?.length || !hasThpcOptionsForSelectedType() : thpcLoading)}
                isSearchable={true}
                isClearable={true}
                className="custom-select"
                placeholder="Select Therapeutic Area"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '36px',
                    maxHeight: '108px',
                    overflowY: 'auto',
                    whiteSpace: 'normal'
                  })
                }}
              />
            </div>
            <div className="col-4">
              <label>Disease State</label>
              <Select
                id="selectedDsst"
                value={formatOptionsForDropdown(cascadingOptions.disease_state || [], 'dsst').find(option => option.value === selecteddsst) || null}
                onChange={(option) => handledsstChange(option ? option.value : "")}
                options={formatOptionsForDropdown(cascadingOptions.disease_state || [], 'dsst')}
                isSearchable={true}
                isClearable={true}
                isLoading={cascadingLoading.disease_state}
                isDisabled={!selectedthpc}
                placeholder="Select Disease State"
                className="custom-select"
              />
            </div>
            <div className="col-4">
              <label>Molecule</label>
              <Select
                id="selectedMolecule"
                value={formatOptionsForDropdown(cascadingOptions.molecule || [], 'mdpr').find(option => option.value === selectedmoelcule) || null}
                onChange={(option) => handlemoleculeChange(option ? option.value : "")}
                options={formatOptionsForDropdown(cascadingOptions.molecule || [], 'mdpr')}
                isSearchable={true}
                isClearable={true}
                isLoading={cascadingLoading.molecule}
                isDisabled={!selecteddsst}
                placeholder="Select Molecule"
                className="custom-select"
              />
            </div>
            {checkSFRole() ? (
              <div className="col col-12 col-md-4 mt-3">
                Select Quarter<sup>*</sup>
                <LdsSelect
                  id="list_quarter"
                  name="list_quarter"
                  options={[
                    {
                      label: "Select",
                      value: "",
                    },
                    {
                      label: "Q1",
                      value: "Q1",
                    },
                    {
                      label: "Q2",
                      value: "Q2",
                    },
                    {
                      label: "Q3",
                      value: "Q3",
                    },
                    {
                      label: "Q4",
                      value: "Q4",
                    },
                  ]}
                  required={true}
                  value={listQuarter}
                  onChange={handleQuarter}
                ></LdsSelect>
              </div>
            ) : (
              <></>
            )}
            {renderRcSourceDropdown()}
          </div>
        )}
      <div className="row next-btn-blk">
        <div className="col col-12 col-md">
          <LdsButton
            classes={`${
              isNextButtonDisabled || selectedDepartment === "" || (modalType && loading)
                ? "outline secondary"
                : "blue-btn"
            }`}
            disabled={isNextButtonDisabled || selectedDepartment === "" || (modalType && loading)}
            onClick={createNext}
          >
            {modalType && loading ? "Loading..." : "Next"}
          </LdsButton>
        </div>
      </div>
    </LdsModal>
  );
};

export default CreateListModal;

